// Вкладки реестра по состоянию: «На вахте 133 · Межвахта 96 · Резерв 84 · Уволены 2 · Все 315».
//
// Стоят над поиском, в слоте `note` колонки списка. Это ОТБОР, а не новое представление
// (правило 2 ПРАВИЛ): строка человека от вкладки не меняется, меняется только то, кого из
// выборки показывать. Поэтому вкладки живут рядом с представлениями по типу оформления, а не
// вместо них.
//
// Числа приходят готовыми и посчитаны из того же ответа, что и строки под вкладками
// (`registry-tabs.ts`): переключение мгновенное, число на вкладке и число строк разойтись не
// могут. Пока данных нет вовсе, на месте вкладок стоит скелет той же высоты: иначе поиск и
// список съезжали бы вниз в момент, когда приедет ответ.
//
// ## Число над подписью, а не рядом с ней
//
// Первая версия ставила число в строку с названием («На вахте 133»). На живых цифрах такая полоса
// не влезала в колонку списка: замер на стенде 01.10.2026, пять вкладок с числами 383 точки
// против нужных 454, а когда в «Отпуске» кто-то есть, их шесть. Полоса прокручивалась, первая
// вкладка уезжала за край и оставляла обрубок («2» от «162»), «Уволены 25» читалось как «Уволены
// 2», а «Все 289» пряталась вовсе. Человек, который достаёт «Все», не должен их искать.
//
// Теперь вкладка двухстрочная: число крупно сверху, подпись мелко снизу. Ширина вкладки равна
// ширине ПОДПИСИ, число её не расширяет, и все вкладки, включая семь («Отпуск» и «Больничный»
// вместе), помещаются в 383 точки колонки без прокрутки (замер настоящим шрифтом: 327 точек
// самих вкладок и по 8 между ними). Порядок в разметке прежний, подпись и за ней число
// («На вахте 133»): читалка с экрана и поиск по имени вкладки ничего не замечают, а вверх число
// переставляет `flex-col-reverse`.
//
// ## Если всё же не влезло
//
// Четырёхзначные числа при семи вкладках дадут около 350 точек самих вкладок и не поместятся.
// Тогда работает общая полоса с прокруткой (`CardTabsList`): стрелки по краям, заслоняющие то, что
// ушло за край, и активная вкладка по центру. Обрубков при этом не видно: край закрыт стрелкой.
//
// ## Высота и палец
//
// Полоса 40 точек, как и была, и столько же у стрелок общей полосы. Область нажатия под палец
// 44 точки даёт `цель-тача`, а в узком окне (портрет планшета, список на весь экран) места
// вдоволь: там вкладка 44 точки видимых и шрифт покрупнее.

import { CardTabsList } from '@/shared/ui/card-tabs';
import { Skeleton } from '@/shared/ui/screen-state';
import * as TabMenu from '@/shared/ui/tab-menu-horizontal';

import type { RegistryTab } from '../route';
import type { TabCounts } from '../registry-tabs';
import { REGISTRY_TAB_TITLES, visibleTabs } from '../registry-tabs';

export interface ВкладкиСостоянийProps {
  /** Открытая вкладка: её выводит адрес, а не данные. */
  активная: RegistryTab;
  счёт: TabCounts;
  onChange: (вкладка: RegistryTab) => void;
}

export function ВкладкиСостояний({ активная, счёт, onChange }: ВкладкиСостоянийProps) {
  const вкладки = visibleTabs(счёт, активная);
  return (
    <TabMenu.Root
      value={активная}
      onValueChange={(значение) => {
        // Radix отдаёт строку; вкладок ровно те, что нарисованы, и чужого значения тут не бывает.
        const вкладка = вкладки.find((код) => код === значение);
        if (вкладка !== undefined) {
          onChange(вкладка);
        }
      }}
    >
      {/* `justify-between`: пять вкладок расходятся по всей ширине ровно, а семь садятся впритык,
          но не ближе восьми точек (`gap-2`). Стоять слева с зазором 20, как у вкладок
          представлений, нельзя: семь вкладок так не влезают. В узком окне места много, и там
          вкладки стоят слева с обычным зазором, а не разъезжаются на семьсот точек.

          `overflow-x-clip`: на планшете у каждой вкладки есть невидимая область нажатия в 44
          точки (`цель-тача`), а вкладка «Все» уже неё (30 точек) и выпускает её за правый край
          полосы на семь. Невидимое тоже считается переполнением: полоса получала лишние семь
          точек прокрутки, зажигала стрелку и закрывала ею «Все» (замер на iPad, 01.10.2026).
          Обрезка по горизонтали снимает вылет, настоящее переполнение она не прячет. */}
      <CardTabsList
        подпись='Состояние людей'
        className='h-10 justify-between gap-2 overflow-x-clip border-t-0 узко:h-11 узко:justify-start узко:gap-6'
      >
        {вкладки.map((код) => (
          <TabMenu.Trigger
            key={код}
            value={код}
            // Подпись 11 точек и без разрядки: это единственная ступень мельче двенадцати в
            // теме, и меньше неё в продукте нет текста. В узком окне подпись обычная, 12.
            className='h-full shrink-0 flex-col-reverse gap-0 py-0 text-subheading-2xs tracking-normal узко:text-label-xs'
          >
            <span>{REGISTRY_TAB_TITLES[код]}</span>
            {/* Пробел нужен чтению с экрана («На вахте 133»), а не глазу: во flex-строке
                пробельный текст не рисуется. */}{' '}
            <span className='text-label-md font-semibold leading-5 tabular-nums'>{счёт[код]}</span>
          </TabMenu.Trigger>
        ))}
      </CardTabsList>
    </TabMenu.Root>
  );
}

/**
 * Скелет полосы вкладок: пять столбиков «число и подпись» по ширине будущих. Показывается, только
 * пока данных нет даже в кэше прошлой сессии (правило 11), и той же высоты, что сама полоса.
 */
export function ВкладкиСостоянийСкелет() {
  return (
    <div
      aria-hidden='true'
      className='flex h-10 items-center justify-between gap-2 border-b border-stroke-soft-200 узко:h-11 узко:justify-start узко:gap-6'
    >
      {['w-12', 'w-14', 'w-10', 'w-12', 'w-8'].map((ширина, номер) => (
        <div key={номер} className='flex flex-col items-center gap-1'>
          <Skeleton className='h-4 w-6' />
          <Skeleton className={`h-3 ${ширина}`} />
        </div>
      ))}
    </div>
  );
}
